import { useSelector } from 'react-redux';
import { DateTime } from 'luxon';
import { getOrganizationLocale } from 'shared/store/selectors/organization.selector';
import { getRegion } from 'shared/store/selectors/user.selector';
import { REGIONS } from 'shared/constants/region.constants';
import {
  DEFAULT_TIMEZONE,
  formatLongDate,
  formatShortTime,
  getTimezoneAbbr,
  isSameOffsetAtDate,
} from 'shared/utils/date.utils';

const LOCALE_BY_REGION = {
  [REGIONS.EU]: 'en-GB',
  [REGIONS.US]: 'en-US',
};

const deviceTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

export function useEducationalEventFormat() {
  const region = useSelector(getRegion);
  const locale = useSelector(getOrganizationLocale) || LOCALE_BY_REGION[region];
  const orgTimezone = useSelector(({ organization }) => organization?.timezone);

  const formatDateWithTime = date => {
    if (!date) return '';
    const day = formatLongDate({ date, locale, options: { year: undefined } });
    const time = formatShortTime({ date, locale });
    const tz = getTimezoneAbbr({ date, timeZone: deviceTimezone, locale });
    return tz ? `${day}, ${time} (${tz})` : `${day}, ${time}`;
  };

  const formatTime = date => {
    if (!date) return '';
    const time = formatShortTime({ date, locale }).toLowerCase();
    const tz = getTimezoneAbbr({ date, timeZone: deviceTimezone, locale });
    return tz ? `${time} (${tz})` : time;
  };

  return {
    formatDayWithWeekday: date =>
      date ? formatLongDate({ date, locale, options: { weekday: 'short' } }) : '',
    formatOrgDayWithWeekday: date =>
      date
        ? formatLongDate({
            date,
            locale,
            options: { weekday: 'short', timeZone: orgTimezone || DEFAULT_TIMEZONE },
          })
        : '',
    formatDateWithTime,
    formatTime,
    /** Time only when `date` falls on today (device zone), otherwise date and time. */
    formatTimeOrDateTime: date => {
      if (!date) return '';
      return DateTime.fromISO(date).hasSame(DateTime.now(), 'day')
        ? formatTime(date)
        : formatDateWithTime(date);
    },
    formatOrgTime: date => {
      if (!date || !orgTimezone) return null;
      if (isSameOffsetAtDate({ date, timeZoneA: deviceTimezone, timeZoneB: orgTimezone })) {
        return null;
      }
      const time = formatShortTime({ date, locale, options: { timeZone: orgTimezone } });
      const tz = getTimezoneAbbr({ date, timeZone: orgTimezone, locale });
      return tz ? `${time} (${tz})` : time;
    },
    orgTimezone,
    orgTimezoneAbbr: date =>
      date && orgTimezone ? getTimezoneAbbr({ date, timeZone: orgTimezone, locale }) : null,
    deviceTimezoneAbbr: date =>
      date ? getTimezoneAbbr({ date, timeZone: deviceTimezone, locale }) : null,
  };
}
